<script setup>
import { ref, computed, watch } from "vue";
import {
  EnvelopeIcon,
  UserIcon,
  ExclamationCircleIcon,
} from "@heroicons/vue/24/outline";

// Props & Emits
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ email: "", subject: "", body: "" }),
  },
});

const emit = defineEmits(["update:modelValue"]);

// Reactive state
const email = ref(props.modelValue.email || "");
const subject = ref(props.modelValue.subject || "");
const body = ref(props.modelValue.body || "");
const emailValidation = ref("");
const subjectValidation = ref("");
const bodyValidation = ref("");

// Computed
const isValidEmail = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.value);
});

const subjectLength = computed(() => subject.value.length);
const bodyLength = computed(() => body.value.length);
const maxSubjectLength = 78;
const maxBodyLength = 2000;

const mailtoUrl = computed(() => {
  if (!email.value) return "";

  let url = `mailto:${encodeURIComponent(email.value)}`;
  const params = [];

  if (subject.value) {
    params.push(`subject=${encodeURIComponent(subject.value)}`);
  }

  if (body.value) {
    params.push(`body=${encodeURIComponent(body.value)}`);
  }

  if (params.length > 0) {
    url += `?${params.join("&")}`;
  }

  return url;
});

// Watchers
watch(
  [email, subject, body],
  ([newEmail, newSubject, newBody]) => {
    const emailData = {
      email: newEmail,
      subject: newSubject,
      body: newBody,
      url: mailtoUrl.value,
    };
    emit("update:modelValue", emailData);
    validateForm();
  },
  { immediate: true }
);

// Methods
const validateForm = () => {
  // Email validation
  if (!email.value) {
    emailValidation.value = "Email address is required";
  } else if (!isValidEmail.value) {
    emailValidation.value = "Invalid email format";
  } else {
    emailValidation.value = "";
  }

  // Subject validation
  if (subject.value.length > maxSubjectLength) {
    subjectValidation.value = `Subject too long (max ${maxSubjectLength} characters)`;
  } else {
    subjectValidation.value = "";
  }

  // Body validation
  if (body.value.length > maxBodyLength) {
    bodyValidation.value = `Body too long (max ${maxBodyLength} characters)`;
  } else {
    bodyValidation.value = "";
  }
};

const clearForm = () => {
  email.value = "";
  subject.value = "";
  body.value = "";
};

const useTemplate = (template) => {
  subject.value = template.subject;
  body.value = template.body;
};

const emailTemplates = [
  {
    name: "Contact Request",
    subject: "Contact Request",
    body: "Hi,\n\nI would like to get in touch regarding...\n\nBest regards,",
  },
  {
    name: "Feedback",
    subject: "Feedback",
    body: "Hi,\n\nI wanted to share some feedback about...\n\nThank you!",
  },
  {
    name: "Support Request",
    subject: "Support Request",
    body: "Hi,\n\nI need help with...\n\nPlease let me know how to proceed.\n\nThanks!",
  },
  {
    name: "Business Inquiry",
    subject: "Business Inquiry",
    body: "Hello,\n\nI am interested in your services and would like to discuss...\n\nLooking forward to hearing from you.",
  },
];
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center space-x-2 mb-4">
      <EnvelopeIcon class="w-5 h-5 text-purple-600" />
      <h3 class="text-lg font-semibold text-gray-800">Email Information</h3>
    </div>

    <!-- Email Address -->
    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700">
        Email Address <span class="text-red-500">*</span>
      </label>

      <div class="relative">
        <div
          class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
        >
          <EnvelopeIcon class="h-5 w-5 text-gray-400" />
        </div>

        <input
          v-model="email"
          type="email"
          placeholder="contact@example.com"
          class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
          :class="{
            'border-red-300 focus:ring-red-500 focus:border-red-500':
              emailValidation,
            'border-green-300 focus:ring-green-500 focus:border-green-500':
              isValidEmail,
          }"
        />
      </div>

      <!-- Email validation -->
      <div
        v-if="emailValidation"
        class="flex items-center space-x-1 text-sm text-red-600"
      >
        <ExclamationCircleIcon class="w-4 h-4" />
        <span>{{ emailValidation }}</span>
      </div>
    </div>

    <!-- Subject -->
    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700">
        Subject (Optional)
      </label>

      <div class="relative">
        <input
          v-model="subject"
          type="text"
          placeholder="Enter email subject"
          :maxlength="maxSubjectLength"
          class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
          :class="{
            'border-red-300 focus:ring-red-500 focus:border-red-500':
              subjectValidation,
          }"
        />

        <!-- Character count -->
        <div class="absolute -bottom-5 right-0 text-xs text-gray-500">
          {{ subjectLength }}/{{ maxSubjectLength }}
        </div>
      </div>

      <!-- Subject validation -->
      <div
        v-if="subjectValidation"
        class="flex items-center space-x-1 text-sm text-red-600 mt-1"
      >
        <ExclamationCircleIcon class="w-4 h-4" />
        <span>{{ subjectValidation }}</span>
      </div>
    </div>

    <!-- Body -->
    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700">
        Message Body (Optional)
      </label>

      <div class="relative">
        <textarea
          v-model="body"
          placeholder="Enter your message here..."
          rows="6"
          :maxlength="maxBodyLength"
          class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
          :class="{
            'border-red-300 focus:ring-red-500 focus:border-red-500':
              bodyValidation,
          }"
        />

        <!-- Character count -->
        <div class="absolute bottom-2 right-2 text-xs text-gray-500">
          {{ bodyLength }}/{{ maxBodyLength }}
        </div>
      </div>

      <!-- Body validation -->
      <div
        v-if="bodyValidation"
        class="flex items-center space-x-1 text-sm text-red-600"
      >
        <ExclamationCircleIcon class="w-4 h-4" />
        <span>{{ bodyValidation }}</span>
      </div>
    </div>

    <!-- Email Templates -->
    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700"
        >Email Templates</label
      >
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          v-for="template in emailTemplates"
          :key="template.name"
          @click="useTemplate(template)"
          class="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors text-left"
          type="button"
        >
          {{ template.name }}
        </button>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex space-x-2">
      <button
        @click="clearForm"
        class="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
        type="button"
      >
        Clear Form
      </button>
    </div>

    <!-- Preview -->
    <div v-if="mailtoUrl" class="bg-purple-50 p-3 rounded-lg">
      <h4 class="text-sm font-medium text-purple-800 mb-1">Email Preview:</h4>
      <p class="text-sm text-purple-700 font-mono break-all">{{ mailtoUrl }}</p>
    </div>

    <!-- Tips -->
    <div class="bg-blue-50 p-3 rounded-lg">
      <h4 class="text-sm font-medium text-blue-800 mb-1">Tips:</h4>
      <ul class="text-sm text-blue-700 space-y-1">
        <li>• Use a valid email address that you check regularly</li>
        <li>• Keep subjects concise and descriptive</li>
        <li>• Long URLs may not work well in QR codes</li>
      </ul>
    </div>
  </div>
</template>